import { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { ChevronLeft, ArrowRight, TrendingUp, Info, Save } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { SkipButton } from '@/components/SkipButton';
import { OnboardingLayout, STARTUP_ONBOARDING_STEPS } from '@/components/OnboardingLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useProfileStore } from '@/lib/state/profile-store';

export default function QuestionnaireTractionScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const params = useLocalSearchParams<{ name?: string; mode?: string }>();

  const isStandalone = params.mode === 'standalone';
  const profile = useProfileStore((s) => s.startupProfile);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);

  const [monthlyRevenue, setMonthlyRevenue] = useState(
    isStandalone && profile?.monthlyRevenue ? profile.monthlyRevenue.toLocaleString() : ''
  );
  const [userCount, setUserCount] = useState(
    isStandalone && profile?.userCount ? profile.userCount.toLocaleString() : ''
  );
  const [growthRate, setGrowthRate] = useState(
    isStandalone && profile?.growthRate ? profile.growthRate.toString() : ''
  );

  const formatCurrency = (value: string): string => {
    const num = value.replace(/[^0-9]/g, '');
    if (!num) return '';
    return Number(num).toLocaleString();
  };

  const handleContinue = () => {
    if (isStandalone && profile) {
      // Standalone mode: save directly to store and go back
      setStartupProfile({
        ...profile,
        monthlyRevenue: Number(monthlyRevenue.replace(/[^0-9]/g, '')) || 0,
        userCount: Number(userCount.replace(/[^0-9]/g, '')) || 0,
        growthRate: Number(growthRate.replace(/[^0-9.]/g, '')) || 0,
      });
      router.back();
      return;
    }

    router.push({
      pathname: '/(onboarding)/questionnaire-market',
      params: {
        ...params,
        monthlyRevenue: monthlyRevenue.replace(/[^0-9]/g, '') || '0',
        userCount: userCount.replace(/[^0-9]/g, '') || '0',
        growthRate: growthRate.replace(/[^0-9.]/g, '') || '0',
      },
    });
  };

  // Form content (shared between mobile and desktop)
  const formContent = (
    <View className="gap-5">
      {/* Info Banner */}
      <View
        className="flex-row items-start gap-3 p-4 rounded-xl"
        style={{ backgroundColor: colors.primary[50] }}
      >
        <Info size={20} color={colors.primary[600]} />
        <Text
          style={{ color: colors.primary[700] }}
          className="text-sm flex-1 leading-relaxed"
        >
          Early-stage startups often don't have metrics yet, and that's okay. Fill in
          what you can — you can always update this later.
        </Text>
      </View>

      {/* Monthly Revenue */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Monthly Revenue (MRR)
        </Text>
        <View
          className="flex-row items-center rounded-xl px-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <Text style={{ color: colors.text.tertiary }} className="text-lg mr-2">
            $
          </Text>
          <TextInput
            style={{
              flex: 1,
              paddingVertical: 16,
              color: colors.text.primary,
              fontSize: 16,
            }}
            placeholder="0"
            placeholderTextColor={colors.text.tertiary}
            value={monthlyRevenue}
            onChangeText={(text) => setMonthlyRevenue(formatCurrency(text))}
            keyboardType="number-pad"
          />
          <Text style={{ color: colors.text.tertiary }} className="text-sm">
            /month
          </Text>
        </View>
      </View>

      {/* User Count */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Active Users / Customers
        </Text>
        <TextInput
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
            borderRadius: 12,
            padding: 16,
            color: colors.text.primary,
            fontSize: 16,
          }}
          placeholder="e.g., 500"
          placeholderTextColor={colors.text.tertiary}
          value={userCount}
          onChangeText={(text) => setUserCount(formatCurrency(text))}
          keyboardType="number-pad"
        />
      </View>

      {/* Growth Rate */}
      <View>
        <Text style={{ color: colors.text.secondary }} className="text-sm mb-2 font-medium">
          Month-over-Month Growth
        </Text>
        <View
          className="flex-row items-center rounded-xl px-4"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: 1,
            borderColor: colors.border.light,
          }}
        >
          <TextInput
            style={{
              flex: 1,
              paddingVertical: 16,
              color: colors.text.primary,
              fontSize: 16,
            }}
            placeholder="e.g., 15"
            placeholderTextColor={colors.text.tertiary}
            value={growthRate}
            onChangeText={setGrowthRate}
            keyboardType="decimal-pad"
          />
          <Text style={{ color: colors.text.tertiary }} className="text-lg ml-2">
            %
          </Text>
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-2">
          Average monthly growth in users or revenue
        </Text>
      </View>

      {/* Pre-revenue option */}
      <AnimatedPressable
        onPress={() => {
          setMonthlyRevenue('0');
          setUserCount('0');
          setGrowthRate('0');
        }}
        className="py-3"
      >
        <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
          I'm pre-revenue / pre-launch
        </Text>
      </AnimatedPressable>

      {/* Continue / Save Button */}
      <View className="mt-4">
        <AnimatedPressable
          onPress={handleContinue}
          className="rounded-xl py-4 flex-row items-center justify-center gap-2"
          style={{ backgroundColor: colors.primary[600] }}
        >
          {isStandalone ? (
            <>
              <Save size={20} color={colors.text.inverse} />
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Save
              </Text>
            </>
          ) : (
            <>
              <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                Continue
              </Text>
              <ArrowRight size={20} color={colors.text.inverse} />
            </>
          )}
        </AnimatedPressable>
      </View>
    </View>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        // Enhance-Profile screen, not part of the canonical onboarding flow —
        // no currentStep id to highlight (see src/lib/onboarding/steps.ts).
        steps={STARTUP_ONBOARDING_STEPS}
        title="Traction Metrics"
        subtitle="Share your current progress (all optional)"
        userType="startup"
        entityName={params.name}
      >
        {formContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          {/* Header */}
          <Animated.View
            entering={FadeInDown.delay(100).duration(500)}
            className="px-5 pt-4 pb-4"
          >
            <View className="flex-row items-center justify-between">
              <Pressable
                onPress={() => router.back()}
                className="w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.primary }}
              >
                <ChevronLeft size={24} color={colors.text.primary} />
              </Pressable>
              <View className="flex-row gap-2">
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.primary[500] }}
                />
                <View
                  className="h-1 w-8 rounded-full"
                  style={{ backgroundColor: colors.border.light }}
                />
              </View>
              <View className="w-10" />
            </View>
          </Animated.View>

          {/* Title */}
          <Animated.View
            entering={FadeInDown.delay(200).duration(500)}
            className="px-5 pb-6"
          >
            <View className="flex-row items-center gap-3 mb-2">
              <TrendingUp size={24} color={colors.primary[600]} />
              <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
                Step 3 of 4
              </Text>
            </View>
            <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
              Traction Metrics
            </Text>
            <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
              Share your current progress (all optional)
            </Text>
          </Animated.View>

          {/* Form */}
          <ScrollView
            className="flex-1 px-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <Animated.View entering={FadeInUp.delay(300).duration(500)} className="pb-8">
              {formContent}
            </Animated.View>
          </ScrollView>

          {/* Skip Button */}
          <SkipButton
            step="profile-details"
            nextRoute="/(onboarding)/complete"
            label="Skip remaining steps"
            delay={500}
          />
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}
